import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaybooks, createPlaybook, aiGeneratePlaybook } from '../../hooks/useSecOps';
import { Sparkles } from 'lucide-react';

interface Playbook {
  id: string;
  name: string;
  description: string;
  active: boolean;
  steps: Array<{ order: number; action_type: string; title: string }>;
  execution_count: number;
  last_executed_at: string;
  created_at: string;
}

export default function PlaybooksPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', steps: '' });
  const [aiForm, setAIForm] = useState({ context: '', severity_focus: '' as '' | 'critical' | 'high' | 'medium' });
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const { data, loading, error, refresh } = usePlaybooks();
  const playbooks = (data?.playbooks ?? []) as Playbook[];

  async function handleCreate() {
    if (!form.name) return;
    setCreating(true);
    try {
      const steps = form.steps.split('\n').filter(s => s.trim()).map((s, i) => ({ order: i + 1, title: s.trim(), action_type: 'manual' }));
      await createPlaybook({ name: form.name, description: form.description, steps });
      setShowCreate(false);
      setForm({ name: '', description: '', steps: '' });
      refresh();
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  async function handleAIGenerate() {
    if (!aiForm.context.trim()) return;
    setGenerating(true);
    setAIError(null);
    try {
      await aiGeneratePlaybook({
        context: aiForm.context,
        severity_focus: aiForm.severity_focus || undefined,
      });
      setShowAIGenerate(false);
      setAIForm({ context: '', severity_focus: '' });
      refresh();
    } catch (err) {
      setAIError(err instanceof Error ? err.message : 'Failed to generate playbook');
    } finally {
      setGenerating(false);
    }
  }

  async function toggleActive(pb: Playbook) {
    setToggling(pb.id);
    try {
      const { secopsApi } = await import('../../lib/secops-api');
      await secopsApi.put(`/secops/playbooks/${pb.id}`, { active: !pb.active });
      refresh();
    } catch { /* handled */ } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">IR Playbooks</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAIGenerate(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
            <Sparkles className="w-4 h-4" />
            AI Generate
          </button>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Create Playbook
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {playbooks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No playbooks yet. Create your first incident response playbook to standardize your IR process.</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => setShowAIGenerate(true)} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
              <Sparkles className="w-4 h-4" />
              AI Generate
            </button>
            <button onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
              Create Playbook
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {playbooks.map(pb => (
            <div key={pb.id} className="bg-white rounded-lg shadow p-6 hover:ring-2 hover:ring-indigo-100 transition">
              <div className="flex items-start justify-between mb-3">
                <div className="cursor-pointer" onClick={() => navigate(`/secops/playbooks/${pb.id}`)}>
                  <h3 className="text-base font-semibold text-gray-900 hover:text-indigo-600">{pb.name}</h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{pb.description || 'No description'}</p>
                </div>
                <button
                  onClick={() => toggleActive(pb)}
                  disabled={toggling === pb.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pb.active ? 'bg-indigo-600' : 'bg-gray-300'} ${toggling === pb.id ? 'opacity-50' : ''}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${pb.active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Steps preview */}
              {pb.steps && pb.steps.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Steps ({pb.steps.length})</p>
                  <div className="space-y-1">
                    {pb.steps.slice(0, 3).map((step, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 flex-shrink-0">{step.order}</span>
                        <span className="truncate">{step.title}</span>
                      </div>
                    ))}
                    {pb.steps.length > 3 && <p className="text-xs text-gray-400 ml-7">+{pb.steps.length - 3} more steps</p>}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-3">
                <span>Executed {pb.execution_count ?? 0} times</span>
                <span>{pb.active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Playbook</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Playbook name..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Describe the playbook..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Steps (one per line)</label>
                <textarea value={form.steps} onChange={e => setForm(f => ({ ...f, steps: e.target.value }))}
                  rows={6} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                  placeholder={"Identify affected systems\nContain the threat\nCollect evidence\nEradicate root cause\nRecover and validate\nPost-incident review"} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !form.name}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Playbook'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Generate Modal */}
      {showAIGenerate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAIGenerate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Playbook Generator
            </h3>
            <p className="text-sm text-gray-500 mb-4">Describe the threat scenario and AI will generate a complete playbook with response steps.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Describe the scenario *</label>
                <textarea
                  value={aiForm.context}
                  onChange={e => setAIForm(f => ({ ...f, context: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder={"e.g., Generate a playbook for Business Email Compromise (BEC) attacks targeting executives with wire transfer fraud attempts"}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity Focus (optional)</label>
                <select
                  value={aiForm.severity_focus}
                  onChange={e => setAIForm(f => ({ ...f, severity_focus: e.target.value as '' | 'critical' | 'high' | 'medium' }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Any severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                </select>
              </div>
            </div>
            {aiError && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-red-700 text-sm">{aiError}</div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => { setShowAIGenerate(false); setAIError(null); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleAIGenerate} disabled={generating || !aiForm.context.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {generating ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Playbook
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
